// Keyboard shortcuts hook for workspace operations

import { useEffect, useCallback } from 'react';

export interface KeyboardShortcutHandlers {
  onSave?: () => void;
  onLoad?: () => void;
  onNew?: () => void;
  onExport?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
}

/**
 * Hook for handling keyboard shortcuts in the workspace system
 */
export function useKeyboardShortcuts(handlers: KeyboardShortcutHandlers) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only handle shortcuts when not typing in input fields
      const isInputFocused =
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement ||
        (event.target as HTMLElement)?.contentEditable === 'true';

      if (isInputFocused) return;

      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      if (isCtrlOrCmd) {
        switch (event.key.toLowerCase()) {
          case 's':
            event.preventDefault();
            if (event.shiftKey) {
              // Ctrl+Shift+S: Save As (could open save modal with template option)
              handlers.onSave?.();
            } else {
              // Ctrl+S: Save current workspace
              handlers.onSave?.();
            }
            break;

          case 'o':
            event.preventDefault();
            // Ctrl+O: Open/Load workspace
            handlers.onLoad?.();
            break;

          case 'n':
            event.preventDefault();
            // Ctrl+N: New analysis (clear workspace)
            handlers.onNew?.();
            break;

          case 'e':
            if (event.shiftKey) {
              event.preventDefault();
              // Ctrl+Shift+E: Export workspace
              handlers.onExport?.();
            }
            break;

          case 'z':
            event.preventDefault();
            if (event.shiftKey) {
              // Ctrl+Shift+Z: Redo
              handlers.onRedo?.();
            } else {
              // Ctrl+Z: Undo
              handlers.onUndo?.();
            }
            break;
        }
      }

      // ESC key for closing modals/panels
      if (event.key === 'Escape') {
        // This could be handled by modal components themselves
        // or we could add a handler for it here
      }
    },
    [handlers]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Return helper text for displaying shortcuts to users
  return {
    shortcuts: [
      { key: 'Ctrl+S', description: 'Save workspace' },
      { key: 'Ctrl+O', description: 'Load workspace' },
      { key: 'Ctrl+N', description: 'New analysis' },
      { key: 'Ctrl+Shift+E', description: 'Export workspace' },
      { key: 'Ctrl+Z', description: 'Undo changes' },
      { key: 'Ctrl+Shift+Z', description: 'Redo changes' },
    ],
  };
}

/**
 * Component for displaying keyboard shortcuts help
 */
export function KeyboardShortcutsHelp() {
  const { shortcuts } = useKeyboardShortcuts({});

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-900 mb-3">Keyboard Shortcuts</h4>
      <div className="space-y-2">
        {shortcuts.map((shortcut, index) => (
          <div key={index} className="flex justify-between items-center text-sm">
            <span className="text-gray-600">{shortcut.description}</span>
            <code className="bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs font-mono">
              {shortcut.key}
            </code>
          </div>
        ))}
      </div>
    </div>
  );
}
